/**
 * ProductionEntryRegister · P17-UAT-12 REV-A
 *
 * ONE reusable, config-driven READ-ONLY register for department production
 * entries (Warping / Covering / Spandex Warping). Reads the authoritative
 * `cwo_production_entries` collection through the existing
 * `GET /cwo-production-entries?stage_code=<CODE>` API — no new collection,
 * no duplicate production engine, no mutation controls.
 *
 * Period filtering is client-side (small volume, 2000-row endpoint cap).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DocumentNumberDisplay from "@/components/DocumentNumberDisplay";
import { exportToCSV } from "@/lib/csv";
import { printDocument } from "@/lib/print";
import { RowActions } from "@/components/RowActions";
import { filterEntries, summarize } from "@/lib/productionRegister";
import {
  MagnifyingGlassIcon, ArrowsClockwiseIcon, DownloadSimpleIcon,
  PrinterIcon, FactoryIcon, SlidersHorizontalIcon,
  XIcon,
} from "@phosphor-icons/react";

// ── Department register configs — the ONLY per-department knobs ──────────
export const REGISTER_CONFIGS = {
  WARPING: {
    stageCode: "WARPING", title: "Warping Production Entry Register",
    csvPrefix: "Warping_Production_Entry_Register", accent: "#22d3ee",
    testId: "warping-register",
  },
  COVERING: {
    stageCode: "COVERING", title: "Covering Production Entry Register",
    csvPrefix: "Covering_Production_Entry_Register", accent: "#f472b6",
    testId: "covering-register",
  },
  SPANDEX_WARPING: {
    stageCode: "SPANDEX_WARPING", title: "Spandex Warping Production Entry Register",
    csvPrefix: "Spandex_Warping_Production_Entry_Register", accent: "#fbbf24",
    testId: "spandex-warping-register",
  },
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const fmtQty = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }));
const timeOf = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};
const dtOf = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString([], { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const STATUS_STYLES = {
  CANONICAL:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  LEGACY:     "bg-amber-500/15 text-amber-300 border-amber-500/40",
  NOT_POSTED: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  UNKNOWN:    "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

const StatusBadge = ({ status, testId }) => (
  <span
    data-testid={testId}
    className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide ${STATUS_STYLES[status] || STATUS_STYLES.UNKNOWN}`}
  >
    {(status || "UNKNOWN").replace("_", " ")}
  </span>
);

export default function ProductionEntryRegister({ config }) {
  const cfg = config || REGISTER_CONFIGS.WARPING;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operators, setOperators] = useState({});

  // Filters (draft = form state, applied = active query)
  const emptyFilters = {
    fromDate: monthStartISO(), toDate: todayISO(),
    entryNo: "", order: "", parentWo: "", cwo: "", rm: "", operator: "", createdBy: "", search: "",
  };
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [showMore, setShowMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active"); // active | reversed | all
  const [viewRow, setViewRow] = useState(null);
  const [reverseRow, setReverseRow] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { hasPerm } = useAuth();
  const canReverse = hasPerm("production_department_entry:reverse");

  const load = () => {
    setLoading(true);
    return Promise.all([
      api.get(`/cwo-production-entries?stage_code=${cfg.stageCode}&status=${statusFilter}`),
      api.get("/operators/").catch(() => ({ data: [] })),
    ])
      .then(([entriesRes, opsRes]) => {
        setRows(Array.isArray(entriesRes.data) ? entriesRes.data : []);
        const map = {};
        (Array.isArray(opsRes.data) ? opsRes.data : []).forEach((o) => { map[o.id] = o.name || o.code || o.id; });
        setOperators(map);
      })
      .catch(() => toast.error("Failed to load production entries"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/cwo-production-entries?stage_code=${cfg.stageCode}&status=${statusFilter}`)
      .then((r) => { if (active) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (active) toast.error("Failed to load production entries"); })
      .finally(() => { if (active) setLoading(false); });
    api.get("/operators/").then((r) => {
      if (!active) return;
      const map = {};
      (Array.isArray(r.data) ? r.data : []).forEach((o) => { map[o.id] = o.name || o.code || o.id; });
      setOperators(map);
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.stageCode, statusFilter]);

  // Reset filters when switching department.
  useEffect(() => {
    setDraft(emptyFilters); setApplied(emptyFilters); setShowMore(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.stageCode]);

  const opName = (id) => (id && operators[id]) || id || "—";

  const filtered = useMemo(
    () => filterEntries(rows, applied, opName),
    [rows, applied, operators] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // UOM-safe period summary — never aggregate unlike units.
  const summary = useMemo(() => summarize(filtered), [filtered]);

  const applyFilters = () => setApplied(draft);
  const resetFilters = () => { setDraft(emptyFilters); setApplied(emptyFilters); };

  const isReversed = (r) => r?.entry_status === "REVERSED" || r?.void || r?.status === "Voided";

  const printEntry = (r) => {
    const reversed = isReversed(r);
    printDocument({
      kind: `${cfg.title.replace(" Production Entry Register", "").toUpperCase()} PRODUCTION ENTRY`,
      no: r.entry_no || r.id,
      meta: [
        { label: "Entry Date", value: r.entry_date || "—" },
        { label: "Entry Time", value: timeOf(r.created_at) },
        { label: "Status", value: reversed ? "REVERSED" : "ACTIVE" },
        { label: "Shift", value: r.shift || "—" },
        { label: "SO / ESO", value: r.source_order_no || "—" },
        { label: "Parent WO", value: r.parent_wo_no || "—" },
        { label: "CWO", value: r.cwo_no || "—" },
        { label: "Operator", value: opName(r.operator_id) },
        { label: "V2 Posting", value: r.v2_posting_status || "—" },
        { label: "Created By", value: r.created_by || "—" },
      ],
      sections: [
        ...(reversed ? [{
          heading: `REVERSED — ${r.void_reason || r.reversal_reason || "reversed"}`,
          columns: [{ key: "k", label: "Audit Note" }],
          rows: [{ k: "This production entry has been REVERSED / VOIDED — not an active production record." }],
        }] : []),
        {
          heading: "Input Material",
          columns: [
            { key: "rm_code", label: "Input RM" },
            { key: "lots", label: "Actual Lot / Batch" },
            { key: "consume", label: "Consumed", align: "right" },
            { key: "uom", label: "UOM" },
          ],
          rows: [{ rm_code: r.rm_code, lots: (r.input_lots || []).join(", ") || "—", consume: fmtQty(r.total_rm_consumption), uom: r.uom }],
        },
        {
          heading: "Production Output",
          columns: [
            { key: "out", label: "Output SFW / SFG" },
            { key: "batch", label: "Output Batch" },
            { key: "produced", label: "Produced", align: "right" },
            { key: "wastage", label: "Wastage", align: "right" },
            { key: "uom", label: "UOM" },
          ],
          rows: [{ out: r.output_sf_code || "—", batch: r.output_batch || "—", produced: fmtQty(r.produced_qty), wastage: fmtQty(r.wastage_qty), uom: r.uom }],
        },
      ],
      signatures: ["Prepared By", "Verified By", "Authorised"],
      footer: `${cfg.title} — read-only production entry document${reversed ? " · REVERSED" : ""}`,
    });
  };

  const submitReverse = async () => {
    if (reverseReason.trim().length < 4) return toast.error("Reversal reason is required (min 4 characters)");
    setBusy(true);
    try {
      const { data } = await api.post(`/cwo-production-entries/${reverseRow.id}/reverse-entry`, { reason: reverseReason.trim() });
      toast.success(`${data.cwo_no || reverseRow.entry_no} reversed · ${data.reversal_legs ?? 0} leg(s), V2 ${data.v2_compensated ?? 0}`);
      setReverseRow(null); setReverseReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reversal failed");
    } finally { setBusy(false); }
  };

  const csvColumns = [
    { key: "entry_no", label: "Production Entry No." },
    { key: "entry_date", label: "Entry Date" },
    { key: "created_at", label: "Entry Time", fmt: (v) => timeOf(v) },
    { key: "source_order_no", label: "SO / ESO No." },
    { key: "parent_wo_no", label: "Parent WO" },
    { key: "cwo_no", label: "CWO No." },
    { key: "rm_code", label: "Input RM" },
    { key: "input_lots", label: "Input Lot / Batch", fmt: (v) => (v || []).join(" | ") },
    { key: "output_sf_code", label: "Output SFW / SFG" },
    { key: "output_batch", label: "Output Batch" },
    { key: "produced_qty", label: "Produced Qty" },
    { key: "wastage_qty", label: "Wastage Qty" },
    { key: "total_rm_consumption", label: "Total RM Consumption" },
    { key: "uom", label: "UOM" },
    { key: "shift", label: "Shift" },
    { key: "operator_id", label: "Operator", fmt: (v) => opName(v) },
    { key: "v2_posting_status", label: "V2 Posting Status" },
    { key: "created_by", label: "Created By" },
    { key: "updated_at", label: "Last Updated", fmt: (v) => dtOf(v) },
  ];

  const onExport = () => {
    if (!filtered.length) return toast.error("Nothing to export for the selected period");
    exportToCSV(`${cfg.csvPrefix}_${applied.fromDate}_${applied.toDate}.csv`, filtered, csvColumns);
  };

  const onPrint = () => {
    if (!filtered.length) return toast.error("Nothing to print for the selected period");
    const appliedList = Object.entries({
      "From": applied.fromDate, "To": applied.toDate, "Entry No": applied.entryNo,
      "SO/ESO": applied.order, "Parent WO": applied.parentWo, "CWO": applied.cwo,
      "RM": applied.rm, "Operator": applied.operator, "Created By": applied.createdBy,
      "Search": applied.search,
    }).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ");
    const summaryLines = Object.entries(summary.byUom)
      .map(([u, s]) => `${u}: Produced ${fmtQty(s.produced)} · Wastage ${fmtQty(s.wastage)} · Consumption ${fmtQty(s.consumption)}`)
      .join("   |   ");
    printDocument({
      kind: cfg.title,
      no: `${applied.fromDate} → ${applied.toDate}`,
      meta: [
        { label: "Period", value: `${applied.fromDate} to ${applied.toDate}` },
        { label: "Generated", value: dtOf(new Date().toISOString()) },
        { label: "Entries", value: String(summary.count) },
        { label: "Totals", value: summaryLines || "—" },
      ],
      sections: [{
        heading: appliedList ? `Filters — ${appliedList}` : "All entries in period",
        columns: [
          { key: "entry_no", label: "Entry No", mono: true },
          { key: "entry_date", label: "Date" },
          { key: "source_order_no", label: "SO/ESO", mono: true },
          { key: "cwo_no", label: "CWO", mono: true },
          { key: "rm_code", label: "Input RM" },
          { key: "input_lots", label: "Lot", fmt: (v) => (v || []).join(", ") },
          { key: "produced_qty", label: "Produced", align: "right" },
          { key: "wastage_qty", label: "Wastage", align: "right" },
          { key: "total_rm_consumption", label: "Consumption", align: "right" },
          { key: "uom", label: "UOM" },
          { key: "v2_posting_status", label: "V2 Status" },
        ],
        rows: filtered,
      }],
      signatures: ["Prepared By", "Verified By", "Authorised"],
      footer: `${cfg.title} — read-only operational register`,
    });
  };

  const Field = ({ label, k, placeholder, type = "text" }) => (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</label>
      <input
        type={type}
        data-testid={`${cfg.testId}-filter-${k}`}
        value={draft[k]}
        placeholder={placeholder || ""}
        onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
        onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
        className="rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-sm text-zinc-100 outline-none focus:border-zinc-500"
      />
    </div>
  );

  return (
    <div data-testid={cfg.testId} className="space-y-6 p-1">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl border" style={{ borderColor: `${cfg.accent}55`, background: `${cfg.accent}14` }}>
            <FactoryIcon size={22} weight="bold" style={{ color: cfg.accent }} />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight text-white" data-testid={`${cfg.testId}-title`}>{cfg.title}</h1>
            <p className="text-xs text-zinc-400">Read-only operational register · authoritative source: production entries</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={`${cfg.testId}-export-csv`} onClick={onExport} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-100 hover:border-zinc-500">
            <DownloadSimpleIcon size={16} weight="bold" /> Export CSV
          </button>
          <button data-testid={`${cfg.testId}-print`} onClick={onPrint} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-100 hover:border-zinc-500">
            <PrinterIcon size={16} weight="bold" /> Print
          </button>
        </div>
      </div>

      {/* Filters — compact single-row primary bar + collapsible More Filters */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">From Date</label>
            <input type="date" data-testid={`${cfg.testId}-filter-fromDate`} value={draft.fromDate}
              onChange={(e) => setDraft((d) => ({ ...d, fromDate: e.target.value }))}
              className="h-9 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 text-sm text-zinc-100 outline-none focus:border-zinc-500" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">To Date</label>
            <input type="date" data-testid={`${cfg.testId}-filter-toDate`} value={draft.toDate}
              onChange={(e) => setDraft((d) => ({ ...d, toDate: e.target.value }))}
              className="h-9 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 text-sm text-zinc-100 outline-none focus:border-zinc-500" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">Status</label>
            <select data-testid={`${cfg.testId}-filter-status`} value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 text-sm text-zinc-100 outline-none focus:border-zinc-500">
              <option value="active">Active</option>
              <option value="reversed">Reversed</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="flex min-w-[220px] flex-1 flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-400">Universal Search</label>
            <div className="relative">
              <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid={`${cfg.testId}-filter-search`}
                value={draft.search}
                onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
                placeholder="Search PRD / SO-ESO / WO / CWO / RM / lot / output…"
                className="h-9 w-full rounded-md border border-zinc-700 bg-zinc-900 pl-9 pr-3 text-sm text-zinc-100 placeholder:text-zinc-500 outline-none focus:border-zinc-500"
              />
            </div>
          </div>
          <button data-testid={`${cfg.testId}-apply`} onClick={applyFilters} className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-bold text-zinc-950" style={{ background: cfg.accent }}>
            <MagnifyingGlassIcon size={16} weight="bold" /> Search / Apply
          </button>
          <button data-testid={`${cfg.testId}-reset`} onClick={resetFilters} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm font-semibold text-zinc-100 hover:border-zinc-500">
            <ArrowsClockwiseIcon size={16} weight="bold" /> Reset
          </button>
          <button data-testid={`${cfg.testId}-more-filters`} onClick={() => setShowMore((v) => !v)} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm font-semibold text-zinc-100 hover:border-zinc-500">
            <SlidersHorizontalIcon size={16} weight="bold" /> More Filters
          </button>
        </div>
        {showMore && (
          <div data-testid={`${cfg.testId}-more-filters-panel`} className="mt-3 grid grid-cols-2 gap-3 border-t border-zinc-800 pt-3 sm:grid-cols-4">
            <Field label="Operator" k="operator" placeholder="Operator" />
            <Field label="Created By" k="createdBy" placeholder="user@…" />
          </div>
        )}
      </div>

      {/* UOM-safe summary */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4" data-testid={`${cfg.testId}-summary`}>
        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-zinc-400">Period Summary</span>
          <span data-testid={`${cfg.testId}-summary-count`} className="rounded-full bg-zinc-800 px-2 py-0.5 text-xs font-bold text-zinc-100">{summary.count} entries</span>
        </div>
        {summary.count === 0 ? (
          <p className="text-sm text-zinc-500">No production entries found for the selected period.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {Object.entries(summary.byUom).map(([u, s]) => (
              <div key={u} data-testid={`${cfg.testId}-summary-uom-${u}`} className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm">
                <span className="mr-2 font-black" style={{ color: cfg.accent }}>{u}</span>
                <span className="text-zinc-400">Produced</span> <span className="font-semibold text-zinc-100">{fmtQty(s.produced)}</span>
                <span className="mx-2 text-zinc-600">·</span>
                <span className="text-zinc-400">Wastage</span> <span className="font-semibold text-zinc-100">{fmtQty(s.wastage)}</span>
                <span className="mx-2 text-zinc-600">·</span>
                <span className="text-zinc-400">Consumption</span> <span className="font-semibold text-zinc-100">{fmtQty(s.consumption)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Register table */}
      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="min-w-[1400px] w-full text-sm" data-testid={`${cfg.testId}-table`}>
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-900/70 text-left text-[11px] uppercase tracking-wider text-zinc-400">
              <th className="px-3 py-2">Entry No.</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">SO / ESO</th>
              <th className="px-3 py-2">Parent WO</th>
              <th className="px-3 py-2">CWO No.</th>
              <th className="px-3 py-2 min-w-[280px]">Input RM</th>
              <th className="px-3 py-2">Input Lot</th>
              <th className="px-3 py-2 min-w-[280px]">Output SFW/SFG</th>
              <th className="px-3 py-2">Output Batch</th>
              <th className="px-3 py-2 text-right">Produced</th>
              <th className="px-3 py-2 text-right">Wastage</th>
              <th className="px-3 py-2 text-right">Total Consumption</th>
              <th className="px-3 py-2">UOM</th>
              <th className="px-3 py-2">Shift</th>
              <th className="px-3 py-2">Operator</th>
              <th className="px-3 py-2">V2 Status</th>
              <th className="px-3 py-2">Created By</th>
              <th className="px-3 py-2">Last Updated</th>
              <th className="px-3 py-2">Entry Status</th>
              <th className="px-3 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={21} className="px-3 py-10 text-center text-zinc-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={21} className="px-3 py-10 text-center text-zinc-500" data-testid={`${cfg.testId}-empty`}>No production entries found for the selected period.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`${cfg.testId}-row-${r.entry_no}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="px-3 py-2"><DocumentNumberDisplay value={r.entry_no} testId={`${cfg.testId}-entryno-${r.entry_no}`} /></td>
                <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{r.entry_date || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap text-zinc-400">{timeOf(r.created_at)}</td>
                <td className="px-3 py-2"><DocumentNumberDisplay value={r.source_order_no} /></td>
                <td className="px-3 py-2"><DocumentNumberDisplay value={r.parent_wo_no} /></td>
                <td className="px-3 py-2"><DocumentNumberDisplay value={r.cwo_no} /></td>
                <td className="px-3 py-2 min-w-[280px] whitespace-normal break-words text-zinc-200" title={r.rm_code}>{r.rm_code || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-zinc-300">{(r.input_lots || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 min-w-[280px] whitespace-normal break-words text-zinc-200" title={r.output_sf_code || ""}>{r.output_sf_code || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-zinc-300">{r.output_batch || "—"}</td>
                <td className="px-3 py-2 text-right font-semibold text-zinc-100">{fmtQty(r.produced_qty)}</td>
                <td className="px-3 py-2 text-right text-amber-300">{fmtQty(r.wastage_qty)}</td>
                <td className="px-3 py-2 text-right font-semibold text-zinc-100">{fmtQty(r.total_rm_consumption)}</td>
                <td className="px-3 py-2 text-zinc-400">{r.uom || "—"}</td>
                <td className="px-3 py-2 text-zinc-400">{r.shift || "—"}</td>
                <td className="px-3 py-2 text-zinc-300">{opName(r.operator_id)}</td>
                <td className="px-3 py-2"><StatusBadge status={r.v2_posting_status} testId={`${cfg.testId}-status-${r.entry_no}`} /></td>
                <td className="px-3 py-2 text-zinc-400">{r.created_by || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap text-zinc-400">{dtOf(r.updated_at)}</td>
                <td className="px-3 py-2">
                  <span data-testid={`${cfg.testId}-entrystatus-${r.entry_no}`}
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${isReversed(r) ? "border-rose-500/40 bg-rose-500/15 text-rose-300" : "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"}`}>
                    {isReversed(r) ? "REVERSED" : "ACTIVE"}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <RowActions
                    testid={`${cfg.testId}-actions-${r.entry_no}`}
                    items={[
                      { label: "View", testid: `${cfg.testId}-view-${r.entry_no}`, onClick: () => setViewRow(r) },
                      { label: "Print", testid: `${cfg.testId}-print-${r.entry_no}`, onClick: () => printEntry(r) },
                      { label: "Reverse", tone: "reverse", testid: `${cfg.testId}-reverse-${r.entry_no}`, show: canReverse && !isReversed(r), onClick: () => { setReverseRow(r); setReverseReason(""); } },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View modal — READ ONLY */}
      {viewRow && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid={`${cfg.testId}-view-modal`} onClick={() => setViewRow(null)}>
          <div className="mt-10 w-full max-w-3xl rounded-2xl border border-zinc-700 bg-zinc-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{cfg.title.replace(" Register", "")}</div>
                <h2 className="text-lg font-black text-white" data-testid={`${cfg.testId}-view-title`}>{viewRow.entry_no}</h2>
              </div>
              <div className="flex items-center gap-2">
                {isReversed(viewRow) && <span className="rounded-full border border-rose-500/40 bg-rose-500/15 px-3 py-1 text-xs font-black text-rose-300" data-testid={`${cfg.testId}-view-reversed`}>REVERSED</span>}
                <button data-testid={`${cfg.testId}-view-print`} onClick={() => printEntry(viewRow)} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm font-semibold text-zinc-100 hover:border-zinc-500"><PrinterIcon size={15} /> Print</button>
                <button data-testid={`${cfg.testId}-view-close`} onClick={() => setViewRow(null)} className="rounded-lg border border-zinc-700 bg-zinc-900 p-2 text-zinc-300 hover:border-zinc-500"><XIcon size={16} /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              {[
                ["Entry Date", viewRow.entry_date], ["Entry Time", timeOf(viewRow.created_at)],
                ["Department", cfg.stageCode], ["Shift", viewRow.shift || "—"],
                ["Operator", opName(viewRow.operator_id)], ["SO / ESO", viewRow.source_order_no || "—"],
                ["Parent WO", viewRow.parent_wo_no || "—"], ["CWO", viewRow.cwo_no || "—"],
              ].map(([k, v]) => (
                <div key={k}><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className="text-sm font-semibold text-zinc-100 break-words">{v}</div></div>
              ))}
            </div>
            <div className="mt-5">
              <div className="mb-1 text-xs uppercase tracking-wider" style={{ color: cfg.accent }}>Input Material</div>
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 text-sm">
                <div className="break-words text-zinc-100">{viewRow.rm_code || "—"}</div>
                <div className="mt-1 text-zinc-400">Lot(s): <span className="font-mono text-zinc-200">{(viewRow.input_lots || []).join(", ") || "—"}</span> · Consumed: <span className="font-semibold text-zinc-100">{fmtQty(viewRow.total_rm_consumption)} {viewRow.uom}</span></div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className="mb-1 text-xs uppercase tracking-wider" style={{ color: cfg.accent }}>Production Output</div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 text-sm">
                  <div className="break-words text-zinc-100">{viewRow.output_sf_code || "—"}</div>
                  <div className="mt-1 text-zinc-400">Batch: <span className="font-mono text-zinc-200">{viewRow.output_batch || "—"}</span></div>
                  <div className="mt-1 text-zinc-400">Produced: <span className="font-semibold text-zinc-100">{fmtQty(viewRow.produced_qty)} {viewRow.uom}</span></div>
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs uppercase tracking-wider" style={{ color: cfg.accent }}>Wastage & Traceability</div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 text-sm text-zinc-400">
                  <div>Wastage: <span className="font-semibold text-amber-300">{fmtQty(viewRow.wastage_qty)} {viewRow.uom}</span>{viewRow.wastage_reason ? ` · ${viewRow.wastage_reason}` : ""}</div>
                  <div className="mt-1">Total Consumption: <span className="font-semibold text-zinc-100">{fmtQty(viewRow.total_rm_consumption)} {viewRow.uom}</span></div>
                  <div className="mt-1">V2 Posting: <span className="font-semibold text-zinc-100">{viewRow.v2_posting_status}</span></div>
                  <div className="mt-1">Conversion: <span className="font-mono text-zinc-300">{viewRow.v2_conversion_group_id || "—"}</span></div>
                  <div className="mt-1">Created By: <span className="text-zinc-200">{viewRow.created_by || "—"}</span> · {dtOf(viewRow.created_at)}</div>
                  {isReversed(viewRow) && <div className="mt-1 text-rose-300">Reversed: {viewRow.voided_by || viewRow.reversed_by || "—"} · {dtOf(viewRow.voided_at || viewRow.reversed_at)}{(viewRow.void_reason || viewRow.reversal_reason) ? ` · ${viewRow.void_reason || viewRow.reversal_reason}` : ""}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reverse confirmation — reuses the authoritative backend reversal */}
      {reverseRow && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" data-testid={`${cfg.testId}-reverse-modal`} onClick={() => !busy && setReverseRow(null)}>
          <div className="mt-16 w-full max-w-lg rounded-2xl border border-rose-700/50 bg-zinc-950 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-black text-white">Reverse Production Entry</h2>
            <p className="mt-1 text-sm text-zinc-400">Posts an equal-and-opposite ledger reversal (V2 + legacy), restores lot balances and rolls back CWO produced qty. The entry stays visible as REVERSED for audit.</p>
            <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-2 rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 text-sm">
              {[["Entry No", reverseRow.entry_no], ["Department", cfg.stageCode], ["SO/ESO", reverseRow.source_order_no], ["Parent WO", reverseRow.parent_wo_no], ["CWO", reverseRow.cwo_no], ["Output SFW/SFG", reverseRow.output_sf_code], ["Produced", `${fmtQty(reverseRow.produced_qty)} ${reverseRow.uom}`], ["Wastage", `${fmtQty(reverseRow.wastage_qty)} ${reverseRow.uom}`]].map(([k, v]) => (
                <div key={k}><span className="text-zinc-500">{k}: </span><span className="font-semibold text-zinc-100 break-words">{v || "—"}</span></div>
              ))}
            </div>
            <label className="mt-4 block text-[10px] uppercase tracking-wider text-zinc-400">Reversal Reason (required, min 4 chars)</label>
            <textarea data-testid={`${cfg.testId}-reverse-reason`} value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} rows={3}
              className="mt-1 w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-rose-500" placeholder="Why is this entry being reversed?" />
            <div className="mt-4 flex justify-end gap-2">
              <button data-testid={`${cfg.testId}-reverse-cancel`} disabled={busy} onClick={() => setReverseRow(null)} className="rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm font-semibold text-zinc-100 hover:border-zinc-500 disabled:opacity-50">Cancel</button>
              <button data-testid={`${cfg.testId}-reverse-confirm`} disabled={busy || reverseReason.trim().length < 4} onClick={submitReverse} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-500 disabled:opacity-50">{busy ? "Reversing…" : "Confirm Reversal"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
